import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, Loader2, LogOut, Inbox, Mail, Download, RefreshCw, Phone, Calendar, Users, ShoppingBasket,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AdminCommandes from "@/pages/AdminCommandes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "escargholala_admin_token";

const TYPE_LABELS = {
  contact: "Renseignement",
  commande: "Commande",
  visite: "Visite & dégustation",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const LoginForm = ({ onLogged }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogged(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Connexion impossible.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-creme flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="bg-sable rounded-3xl p-8 lg:p-10 shadow-[0_18px_45px_rgba(44,42,40,0.12)] border border-border w-full max-w-md space-y-5"
      >
        <div className="text-center">
          <span className="inline-flex w-14 h-14 rounded-2xl bg-bordeaux/10 text-bordeaux items-center justify-center mb-3">
            <Lock size={26} />
          </span>
          <h1 className="font-heading text-3xl text-foreground">Espace privé</h1>
          <p className="text-foreground/60 text-sm mt-1">Escarg'Holala — Administration</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input data-testid="admin-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="escargholala@gmail.com" className="bg-white" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Mot de passe</Label>
          <Input data-testid="admin-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-white" />
        </div>
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="w-full rounded-full bg-bordeaux text-white font-600 px-7 py-3.5 hover:bg-bordeaux-dark transition-all flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {loading ? <><Loader2 size={18} className="animate-spin" /> Connexion...</> : <>Se connecter</>}
        </button>
      </form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("commandes");
  const [demandes, setDemandes] = useState([]);
  const [subs, setSubs] = useState([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s, o] = await Promise.all([
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/newsletter`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
      ]);
      setDemandes(d.data);
      setSubs(s.data);
      setOrdersCount(o.data.length);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expirée, reconnectez-vous.");
        onLogout();
      } else {
        toast.error("Erreur de chargement.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const exportSubs = () => {
    const csv = "email,date_inscription\n" + subs.map((s) => `${s.email},${s.created_at}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inscrits_newsletter.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-creme">
      <header className="bg-sable border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl text-foreground">Espace privé</h1>
            <p className="text-foreground/55 text-sm">Escarg'Holala — Administration</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-600 text-foreground hover:bg-white transition-colors">
              <RefreshCw size={16} /> Actualiser
            </button>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full bg-bordeaux text-white px-4 py-2 text-sm font-600 hover:bg-bordeaux-dark transition-colors">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-sable rounded-2xl border border-border p-5 flex items-center gap-4">
            <span className="w-12 h-12 rounded-xl bg-bordeaux/10 text-bordeaux flex items-center justify-center"><ShoppingBasket size={22} /></span>
            <div><div className="text-3xl font-heading text-foreground" data-testid="stat-commandes">{ordersCount}</div><div className="text-sm text-foreground/55">Commandes</div></div>
          </div>
          <div className="bg-sable rounded-2xl border border-border p-5 flex items-center gap-4">
            <span className="w-12 h-12 rounded-xl bg-bordeaux/10 text-bordeaux flex items-center justify-center"><Inbox size={22} /></span>
            <div><div className="text-3xl font-heading text-foreground" data-testid="stat-demandes">{demandes.length}</div><div className="text-sm text-foreground/55">Demandes reçues</div></div>
          </div>
          <div className="bg-sable rounded-2xl border border-border p-5 flex items-center gap-4">
            <span className="w-12 h-12 rounded-xl bg-mousse/12 text-mousse flex items-center justify-center"><Users size={22} /></span>
            <div><div className="text-3xl font-heading text-foreground" data-testid="stat-subs">{subs.length}</div><div className="text-sm text-foreground/55">Inscrits newsletter</div></div>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button data-testid="tab-commandes" onClick={() => setTab("commandes")} className={`rounded-full px-5 py-2 text-sm font-600 transition-colors ${tab === "commandes" ? "bg-bordeaux text-white" : "bg-sable border border-border text-foreground hover:bg-white"}`}>
            Commandes
          </button>
          <button data-testid="tab-demandes" onClick={() => setTab("demandes")} className={`rounded-full px-5 py-2 text-sm font-600 transition-colors ${tab === "demandes" ? "bg-bordeaux text-white" : "bg-sable border border-border text-foreground hover:bg-white"}`}>
            Demandes ({demandes.length})
          </button>
          <button data-testid="tab-newsletter" onClick={() => setTab("newsletter")} className={`rounded-full px-5 py-2 text-sm font-600 transition-colors ${tab === "newsletter" ? "bg-bordeaux text-white" : "bg-sable border border-border text-foreground hover:bg-white"}`}>
            Newsletter ({subs.length})
          </button>
        </div>

        {tab === "commandes" && <AdminCommandes token={token} />}

        {loading && tab !== "commandes" && (
          <div className="flex items-center justify-center py-20 text-foreground/50"><Loader2 className="animate-spin mr-2" /> Chargement...</div>
        )}

        {!loading && tab === "demandes" && (
          <div data-testid="demandes-list" className="space-y-4">
            {demandes.length === 0 && <p className="text-foreground/50 text-center py-12">Aucune demande pour le moment.</p>}
            {demandes.map((d) => (
              <div key={d.id} data-testid="demande-card" className="bg-sable rounded-2xl border border-border p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-600 text-foreground">{d.prenom} {d.nom}</span>
                      <span className="text-xs uppercase tracking-wide bg-bordeaux/10 text-bordeaux rounded-full px-2.5 py-0.5 font-600">{TYPE_LABELS[d.type_demande] || d.type_demande}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-foreground/70">
                      <a href={`mailto:${d.email}`} className="inline-flex items-center gap-1.5 hover:text-bordeaux"><Mail size={14} /> {d.email}</a>
                      {d.telephone && <a href={`tel:${d.telephone}`} className="inline-flex items-center gap-1.5 hover:text-bordeaux"><Phone size={14} /> {d.telephone}</a>}
                    </div>
                  </div>
                  <span className="text-xs text-foreground/50 whitespace-nowrap">{fmtDate(d.created_at)}</span>
                </div>
                {d.type_demande === "visite" && (d.nombre_personnes || d.date_souhaitee) && (
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-mousse">
                    {d.nombre_personnes && <span className="inline-flex items-center gap-1.5"><Users size={14} /> {d.nombre_personnes} pers.</span>}
                    {d.date_souhaitee && <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {d.date_souhaitee}</span>}
                  </div>
                )}
                {d.sujet && <div className="mt-3 text-sm font-600 text-foreground/80">Sujet : {d.sujet}</div>}
                <p className="mt-2 text-foreground/75 text-sm whitespace-pre-wrap bg-white rounded-xl p-3 border border-border">{d.message}</p>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "newsletter" && (
          <div data-testid="newsletter-list">
            <div className="flex justify-end mb-4">
              <button data-testid="export-csv" onClick={exportSubs} disabled={subs.length === 0} className="inline-flex items-center gap-2 rounded-full border border-mousse text-mousse px-4 py-2 text-sm font-600 hover:bg-mousse hover:text-white transition-colors disabled:opacity-50">
                <Download size={16} /> Exporter (CSV)
              </button>
            </div>
            {subs.length === 0 ? (
              <p className="text-foreground/50 text-center py-12">Aucun inscrit pour le moment.</p>
            ) : (
              <div className="bg-sable rounded-2xl border border-border overflow-hidden">
                {subs.map((s, i) => (
                  <div key={s.id} data-testid="sub-row" className={`flex items-center justify-between px-5 py-3.5 ${i !== 0 ? "border-t border-border" : ""}`}>
                    <a href={`mailto:${s.email}`} className="text-foreground font-500 hover:text-bordeaux inline-flex items-center gap-2"><Mail size={15} className="text-mousse" /> {s.email}</a>
                    <span className="text-xs text-foreground/50">{fmtDate(s.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginForm onLogged={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
};

export default Admin;
